import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';

import { useAppSelector } from 'src/app/store/hooks';
import { useTripsRetrieveQuery } from 'src/common/external/bambi-api/bambiApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { formatFullName } from 'src/common/util/formatFullName';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { TripStatusUpdatesEditorItem } from './TripStatusUpdatesEditorItem';
import { useLatestTripEventsOfType } from './useLatestTripEventsOfType';

export function TripStatusUpdatesEditor() {
  const tripId = useAppSelector(
    (state) => state.trip.tripStatusUpdatesModal.tripId
  );

  const {
    isEditable,
    data: trip,
    isLoading: isTripLoading,
    error: tripError,
  } = useTripsRetrieveQuery(
    { id: tripId },
    {
      skip: !tripId,
      selectFromResult: (result) => {
        return {
          ...result,
          isEditable: !result.data?.intermediary_id,
        };
      },
    }
  );

  const {
    events,
    isLoading: isEventsLoading,
    error: eventsError,
  } = useLatestTripEventsOfType(tripId);

  if (isTripLoading || isEventsLoading) {
    return <LoadingIndicator />;
  }

  if (tripError || !trip) {
    return (
      <FormErrorMessage>
        Error loading trip (id: {tripId}):{' '}
        {tripError ? formatServerError(tripError) : 'Unknown error'}
      </FormErrorMessage>
    );
  }

  if (eventsError) {
    return (
      <FormErrorMessage>
        Error loading events for trip (id: {tripId}):{' '}
        {formatServerError(eventsError)}
      </FormErrorMessage>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-xl font-bold">Trip Status Updates</h2>
        <p className="text-sm text-gray-500">
          View and edit status update times for {formatFullName(trip.passenger)}
          's trip (id: {trip.confirmation_code || trip.id}){' '}
          {trip.intermediary_id ? `(managed by ${trip.intermediary})` : ''}
        </p>
      </div>
      {!isEditable && (
        <FormErrorMessage>
          This trip is managed by a broker. You cannot edit status updates for
          this trip.
        </FormErrorMessage>
      )}
      {events.length === 0 ? (
        <div>
          No events found for trip id {trip.confirmation_code || trip.id}
        </div>
      ) : (
        <ul
          className="divide-y divide-gray-200"
          data-testid="status-update-form"
        >
          {events.map((event) => (
            <TripStatusUpdatesEditorItem
              key={event.id}
              event={event}
              disableEditing={!isEditable}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
